import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { GameProvider } from "./context/GameContext";
import { NotificationProvider } from "./context/NotificationContext";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import HomePage from "./pages/HomePage";
import OdinAIPage from "./pages/OdinAIPage";
import ExplorePage from "./pages/ExplorePage";
import ReelsPage from "./pages/ReelsPage";
import ProfilePage from "./pages/ProfilePage";
import MessagesPage from "./pages/MessagesPage";
import NotificationsPage from "./pages/NotificationsPage";
import SearchPage from "./pages/SearchPage";
import BookmarksPage from "./pages/BookmarksPage";
import SettingsPage from "./pages/SettingsPage";
import PostDetailPage from "./pages/PostDetailPage";
import TipSuccessPage from "./pages/TipSuccessPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import CreatorDashboardPage from "./pages/CreatorDashboardPage";
import WalletPage from "./pages/WalletPage";
import PremiumSuccessPage from "./pages/PremiumSuccessPage";
import AdsPage from "./pages/AdsPage";
import WorldMapPage from "./pages/WorldMapPage";
import ModerationPage from "./pages/ModerationPage";
import TreasuryPage from "./pages/TreasuryPage";
import BetaGatePage from "./pages/BetaGatePage";
import RegionMapPage from "./pages/RegionMapPage";
import MidgardRegionalMap from "./pages/MidgardRegionalMap";
import SettlementPage from "./pages/SettlementPage";
import LocationPage from "./pages/LocationPage";
import GamePage from "./pages/GamePage";
import GamePage2 from "./pages/GamePage2";
import EnhancedGamePage from "./pages/EnhancedGamePage";
import MinimalTestPage from "./pages/MinimalTestPage";
import SimpleGamePage from "./pages/SimpleGamePage";
import AboutPage from "./pages/AboutPage";
import LegalPage from "./pages/LegalPage";
import InfoShell from "./components/site/InfoShell";

// Layout
import MainLayout from "./components/layout/MainLayout";

import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { betaMode, betaLocked, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20" />
      </div>
    );
  }

  // Beta gate: authenticated but not on the approved list
  if (betaLocked) {
    return <BetaGatePage pending={true} />;
  }

  // Beta gate: unauthenticated visitor, and not on an auth path
  const isAuthPath = location.pathname === '/auth' || location.pathname.startsWith('/auth/');
  // About and legal pages stay readable: people must be able to read the terms before joining.
  const isInfoPath = location.pathname === '/about' || location.pathname.startsWith('/legal/');
  if (betaMode && !user && !isAuthPath && !isInfoPath) {
    return <BetaGatePage pending={false} />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* About and legal: readable signed in or out */}
      <Route element={<InfoShell />}>
        <Route path="/about" element={<AboutPage />} />
        <Route path="/legal/:docId" element={<LegalPage />} />
      </Route>
      
      {/* Authenticated app: one persistent shell (top bar, rails, bottom navigation) */}
      <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route path="/home" element={<HomePage />} />
        <Route path="/odin" element={<OdinAIPage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/reels" element={<ReelsPage />} />
        <Route path="/profile/:username" element={<ProfilePage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/bookmarks" element={<BookmarksPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/post/:postId" element={<PostDetailPage />} />
        <Route path="/creator-dashboard" element={<CreatorDashboardPage />} />
        <Route path="/wallet" element={<WalletPage />} />
        <Route path="/wallet/success" element={<WalletPage />} />
        <Route path="/ads" element={<AdsPage />} />
        <Route path="/tip/success" element={<TipSuccessPage />} />
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="/premium/success" element={<PremiumSuccessPage />} />
        <Route path="/moderation" element={<ModerationPage />} />
        <Route path="/treasury" element={<TreasuryPage />} />

        {/* World navigation: artwork fills the shell's stage, app navigation stays */}
        <Route path="/world" element={<WorldMapPage />} />
        <Route path="/world/midgard" element={<MidgardRegionalMap />} />
        <Route path="/world/midgard/settlement/:settlementId" element={<SettlementPage />} />
        <Route path="/world/midgard/settlement/:settlementId/location/:locationId" element={<LocationPage />} />
        <Route path="/world/:realmId" element={<RegionMapPage />} />
      </Route>

      {/* Full-screen gameplay (the only routes outside the shell); each has its own exit control */}
      <Route path="/world/midgard/play" element={<ProtectedRoute><EnhancedGamePage /></ProtectedRoute>} />
      <Route path="/world/midgard/play/:settlementId" element={<ProtectedRoute><EnhancedGamePage /></ProtectedRoute>} />
      <Route path="/world/midgard/play/:settlementId/:locationId" element={<ProtectedRoute><EnhancedGamePage /></ProtectedRoute>} />
      
      {/* Simple 3D Game Route (legacy, kept for testing) */}
      <Route path="/world/simple-game" element={<ProtectedRoute><SimpleGamePage /></ProtectedRoute>} />
      
      {/* Minimal 3D Test Route */}
      <Route path="/world/test3d" element={<ProtectedRoute><MinimalTestPage /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <GameProvider>
            <BrowserRouter>
              <AppRoutes />
              <Toaster position="top-center" richColors />
            </BrowserRouter>
          </GameProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
